import { useMemo, useState } from "react";
import { LuBuilding2, LuCheck, LuLayers } from "react-icons/lu";
import { useTranslation } from "react-i18next";
import { useBranch } from "../../context/branch";
import './BranchSwitcher.css';

/**
 * Branch picker for the header.
 *
 * Renders nothing for a company with a single branch, so existing customers see
 * no change at all until they actually create a second one.
 */
function BranchSwitcher({ open, setOpen, onOpen }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("");
  const {
    branches,
    activeBranch,
    allBranchesMode,
    isMultiBranch,
    canViewAll,
    switchBranch,
  } = useBranch();

  const visibleBranches = useMemo(() => {
    const query = filter.trim().toLowerCase();

    if (!query) return branches;

    return branches.filter((branch) =>
      branch.name.toLowerCase().includes(query) ||
      branch.code.toLowerCase().includes(query)
    );
  }, [branches, filter]);

  if (!isMultiBranch) {
    return null;
  }

  const currentLabel = allBranchesMode
    ? t('branches.allBranches')
    : (activeBranch ? `${activeBranch.code} — ${activeBranch.name}` : t('branches.selectBranch'));

  return (
    <div className="header-dropdown">
      <div style={{ position: "relative" }}>
        <button
          type="button"
          className={`branch-switcher-btn ${allBranchesMode ? "all-branches" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
            setOpen(!open);
          }}
          title={t('branches.switchBranch')}
        >
          {allBranchesMode ? <LuLayers size={16} /> : <LuBuilding2 size={16} />}
          <span className="branch-switcher-label">{currentLabel}</span>
        </button>

        <div
          className={`dropdown-menu-custom branch-dropdown ${open ? "show" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="dropdown-header">{t('branches.switchBranch')}</div>

          {branches.length > 8 && (
            <div className="branch-filter-wrapper">
              <input
                type="search"
                className="branch-filter-input"
                placeholder={t('branches.filterPlaceholder')}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
          )}

          <div className="branch-list">
            {visibleBranches.map((branch) => (
              <button
                key={branch.id}
                type="button"
                className={`branch-item ${!allBranchesMode && activeBranch?.id === branch.id ? "active" : ""}`}
                onClick={() => switchBranch(branch.id)}
              >
                <span className="branch-item-code">{branch.code}</span>
                <span className="branch-item-name">{branch.name}</span>
                {!allBranchesMode && activeBranch?.id === branch.id && <LuCheck size={15} />}
              </button>
            ))}
          </div>

          {canViewAll && (
            <div className="branch-all-wrapper">
              <button
                type="button"
                className={`branch-item branch-item-all ${allBranchesMode ? "active" : ""}`}
                onClick={() => switchBranch('all')}
              >
                <LuLayers size={15} />
                <span className="branch-item-name">{t('branches.allBranches')}</span>
                {allBranchesMode && <LuCheck size={15} />}
              </button>
              <div className="branch-all-hint">{t('branches.allBranchesHint')}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BranchSwitcher;
